import { FullScreenPageContainer } from '@/components/utils/FullScreenPageContainer';
import useRegisterRouteSafety from '@/hooks/registration/useRegisterRouteSafety';
import useRegisterStore, { RegistrationTrack } from '@/hooks/useRegistrationStore';
import { DataForm } from '@design-system/DataForm/DataForm';
import { useEffect } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import type { ActionButtonsProps } from './ActionButtons';
import { ActionButtons } from './ActionButtons';
import { cn } from '@/utils/styles';
import { useUserStore } from '@/hooks/useUserStore';

export interface RegistrationScreenProps extends ActionButtonsProps {
  form?: UseFormReturn<any, unknown, undefined>;
  formOnValid?: SubmitHandler<any>;
  content?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  postActionsContent?: React.ReactNode;
  previousPath?: string;
  isLoading?: boolean;
  errorMessage?: string | null;
  hideBackButton?: boolean;
  checkForRegisterStore?: RegistrationTrack;
  primartActionDisabled?: boolean;
  isConsentScreen?: boolean;
  actionContainerClassName?: string;
  'data-testid'?: string;
}

export const RegistrationScreen = ({
  form,
  formOnValid = () => {},
  content = null,
  title,
  description = null,
  previousPath,
  primaryActionLabel,
  primaryActionIsSubmit = false,
  primaryActionOnClick,
  secondaryActionLabel,
  secondaryActionOnClick,
  postActionsContent = null,
  isLoading = false,
  errorMessage,
  hideBackButton,
  checkForRegisterStore,
  primartActionDisabled,
  isConsentScreen,
  actionContainerClassName,
  'data-testid': testId,
}: RegistrationScreenProps) => {
  const { restrictedRegistrationSteps, setRestrictedRegistrationSteps } = useRegisterStore();
  const { checkTheRouteAndRegisterStore } = useRegisterRouteSafety();
  const { user: userData } = useUserStore();

  const renderContentAndButtons = () => (
    <>
      {content}
      {errorMessage && (
        <p className={cn("typography-btn-small text-destructive", isConsentScreen && "md:max-w-sm")} data-testid="registrationScreen-errorMessage">{errorMessage}</p>
      )}
      <ActionButtons
        primaryActionLabel={primaryActionLabel}
        primaryActionIsSubmit={primaryActionIsSubmit}
        primaryActionOnClick={primaryActionOnClick}
        secondaryActionLabel={secondaryActionLabel}
        secondaryActionOnClick={secondaryActionOnClick}
        primartActionDisabled={primartActionDisabled}
        primaryActionLoading={isLoading}
        isConsentScreen={isConsentScreen}
        className={actionContainerClassName}
      />
      {postActionsContent}
    </>
  );

  useEffect(() => {
    if (userData && restrictedRegistrationSteps) {
      const tracksToAdd = [
        RegistrationTrack.AccountCreation,
        RegistrationTrack.PersonalInformation
      ].filter(track => !restrictedRegistrationSteps.includes(track));
      
      if (tracksToAdd.length > 0) {
        setRestrictedRegistrationSteps([...restrictedRegistrationSteps, ...tracksToAdd]);
      }
    }
  }, [userData, restrictedRegistrationSteps, setRestrictedRegistrationSteps]);

  useEffect(() => {
    if (checkForRegisterStore) {
      checkTheRouteAndRegisterStore(checkForRegisterStore);
    }
  }, [checkForRegisterStore]);

  return (
    <FullScreenPageContainer
      previousPath={previousPath}
      hideBackButton={hideBackButton}
      data-testid={testId}
    >
      <h2 className="text-[40px] font-sans mb-4 font-normal">{title}</h2>
      {description && (
        <div className="flex">
          <p className="font-sans typography-body text-secondary">{description}</p>
        </div>
      )}

      {form ? (
        <DataForm form={form} onValid={formOnValid} className={!isConsentScreen ? "md:max-w-sm" : ""} data-testid="registrationScreen-form">
          {renderContentAndButtons()}
        </DataForm>
      ) : (
        renderContentAndButtons()
      )}
    </FullScreenPageContainer>
  );
};
